import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, Tag } from '@app/ui';
import shortenStr from '@app/utils/shortenStr';

/** API returns: single "crossfit", multiple "thescorebet,thescorebetca", or null */
function normalizeCollaboratingAccounts(
    value: string | string[] | null | undefined,
): string[] {
    if (value == null) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    const trimmed = String(value).trim();
    if (!trimmed) return [];
    return trimmed
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
}

interface DisplayCollaboratingAccountsProps {
    /** API: single username, comma-separated string, or array (normalized to array) */
    accounts?: string | string[] | null;
}

const DisplayCollaboratingAccounts: React.FC<
    DisplayCollaboratingAccountsProps
> = ({ accounts }) => {
    const { t } = useTranslation('measurement');
    const [expanded, setExpanded] = useState(false);
    const list = useMemo(
        () => normalizeCollaboratingAccounts(accounts),
        [accounts],
    );

    if (list.length === 0) return null;

    const toggleExpanded = () => setExpanded(prev => !prev);

    const first = list.slice(0, 2);
    const rest = list.slice(2);

    const renderTags = (arr: string[]) =>
        arr.map((username, idx) => {
            const shortName = shortenStr(20, username);
            return (
                <React.Fragment key={`${username}-${idx}`}>
                    <Tooltip title={username}>
                        <Tag
                            style={{
                                fontSize: 12,
                                marginBottom: 2,
                                maxWidth: 120,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {shortName}
                        </Tag>
                    </Tooltip>
                    <br />
                </React.Fragment>
            );
        });

    return (
        <>
            {renderTags(first)}
            {!expanded && rest.length > 0 && (
                <Tag
                    style={{ fontSize: 12, cursor: 'pointer' }}
                    onClick={toggleExpanded}
                >
                    {t('socialMediaExposure.table.moreItems', {
                        count: rest.length,
                    })}
                </Tag>
            )}
            {expanded && renderTags(rest)}
        </>
    );
};

export default DisplayCollaboratingAccounts;
